import axios, { AxiosRequestConfig } from 'axios';
import { useTransactionCertificateStore } from '../../store/transaction-certificates.store';
import { Utills } from '../app-configs/utilit';
import { checkTokenExpiry } from '../../app-renderer';
import useNotification from '../hooks/useNotification';
import { notify } from '../utils/notify.utills';

interface ApiResponse<T> {
    data: T;
    status: number;
    statusText: string;
    headers: any;
    config: AxiosRequestConfig;
}

const defaultHeaders = {
    'Content-Type': 'application/json'
};

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: defaultHeaders,
});


// Helper function that waits for the token to become available in the store.
// It polls the store every `interval` milliseconds until the token is found or the timeout is exceeded.
const waitForToken = (timeout = 5000, interval = 100): Promise<string> => {
    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const checkToken = () => {
            const token = useTransactionCertificateStore.getState().authToken;
            if (token) {
                resolve(token);
            } else if (Date.now() - startTime > timeout) {
                reject(new Error('Timeout waiting for auth token'));
            } else {
                setTimeout(checkToken, interval);
            }
        };

        checkToken();
    });
};

axiosInstance.interceptors.request.use(
    async (config) => {
        let token = useTransactionCertificateStore.getState().authToken;
        const newTokenResponse = await checkTokenExpiry()
        if (newTokenResponse.isTokenGenerated) {
            useTransactionCertificateStore.getState().setAuthToken(newTokenResponse.token)
            useTransactionCertificateStore.getState().setTokenExpiry(newTokenResponse.expiry)
            token = newTokenResponse.token
        }
        else if (!token) {
            try {
                token = await waitForToken();
            } catch (error) {
                return Promise.reject(error);
            }
        }

        // Attach the token to the Authorization header
        config.headers['Authorization'] = token;
        return config;
    },
    (error) => {
        console.log(`axios interceptor error ${error}`);
        return Promise.reject(error);
    }
);

// API service methods using the axios instance
const apiService = {
    async get<T>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        Utills.cl(`apiService get initial config ${JSON.stringify(params)}`);
        const sanitizedParams = Utills.sanitizeInput(params);
        const urlWithParams = new URL(url, axiosInstance.defaults.baseURL);
        if (sanitizedParams) {
            Object.entries(sanitizedParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    urlWithParams.searchParams.append(key, String(value));
                }
            });
        }
        const finalConfig = { ...config, headers: { ...defaultHeaders, ...config?.headers } };
        Utills.cl(`apiService get finalConfig ${JSON.stringify(finalConfig)}`);
        const response = await axiosInstance.get<T>(urlWithParams.toString(), finalConfig);
        Utills.cl(`apiService get ${response}`);
        return response;
    },

    async post<T, R>(url: string, data: T, config?: AxiosRequestConfig): Promise<ApiResponse<R>> {
        const sanitizedData = Utills.sanitizeInput(data);
        const finalConfig = { ...config, headers: { ...defaultHeaders, ...config?.headers } };
        const response = await axiosInstance.post<R>(url, sanitizedData, finalConfig);
        return response;
    },

    async put<T, R>(url: string, data?: T, config?: AxiosRequestConfig): Promise<ApiResponse<R>> {
        let sanitizedData;
        let finalConfig;
        
        // Handle different data types appropriately
        if (data instanceof ArrayBuffer || data instanceof FormData || data instanceof File) {
            // Don't sanitize binary data, FormData, or File objects
            sanitizedData = data;
            finalConfig = { ...config }; // Don't merge defaultHeaders for binary data
        } else {
            sanitizedData = Utills.sanitizeInput(data);
            finalConfig = { ...config, headers: { ...defaultHeaders, ...config?.headers } };
        }
        
        const response = await axiosInstance.put<R>(url, sanitizedData, finalConfig);
        return response;
    },

    async delete<T>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        Utills.cl(`apiService delete initial config ${JSON.stringify(params)}`);
        const sanitizedParams = Utills.sanitizeInput(params);
        const urlWithParams = new URL(url, axiosInstance.defaults.baseURL);
        if (sanitizedParams) {
            Object.entries(sanitizedParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    urlWithParams.searchParams.append(key, String(value));
                }
            });
        }
        const finalConfig = { ...config, headers: { ...defaultHeaders, ...config?.headers } };
        const response = await axiosInstance.delete<T>(urlWithParams.toString(), finalConfig);
        return response;
    },
};
export { axiosInstance }; // Named export
export default apiService; // Default export

